import { 
  Target, 
  Clock, 
  MessageCircle, 
  FileText, 
  ClipboardCheck,
  CheckCircle2,
  ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const WHATSAPP_NUMBER = "56929894282";
const WHATSAPP_MESSAGE = encodeURIComponent("Hola Odontofix, quiero cotizar mantención para mi equipo y agendar visita.");
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;
const EMAIL = "contacto@odontofix.cl";

const focusPoints = [
  { icon: Clock, title: "Continuidad operacional", description: "Minimizamos tiempos de inactividad de tus equipos" },
  { icon: MessageCircle, title: "Transparencia y comunicación", description: "Mantenemos informado al cliente en cada etapa" },
  { icon: FileText, title: "Orden documental y trazabilidad", description: "Todo queda registrado y accesible" },
  { icon: ClipboardCheck, title: "Trabajo estandarizado", description: "Checklists e informes consistentes" }
];

const processSteps = [
  { title: "Diagnóstico", description: "Evaluamos el estado de tus equipos" },
  { title: "Plan", description: "Definimos acciones y presupuesto" },
  { title: "Ejecución", description: "Realizamos el trabajo profesional" },
  { title: "Seguimiento", description: "Informe y monitoreo continuo" }
];

export default function Nosotros() {
  return (
    <div data-testid="nosotros-page">
      {/* Hero Section */}
      <section className="bg-[#384091] py-16 md:py-24" data-testid="nosotros-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Sobre Odontofix
              </h1>
              <p className="text-xl text-white/90 leading-relaxed">
                Ayudamos a que tus equipos clínicos estén disponibles, seguros y con trazabilidad completa.
              </p>
            </div>
            <div className="relative hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1643660527072-9c702932f606?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Clínica dental con equipamiento moderno"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Purpose Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="purpose-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-8">
              <Target className="h-10 w-10 text-primary" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-text-primary mb-6">
              Nuestro Propósito
            </h2>
            <p className="text-xl text-text-secondary leading-relaxed">
              Ayudamos a que tus equipos clínicos estén disponibles, seguros y con trazabilidad. 
              Creemos que la mantención planificada y documentada es clave para la continuidad operacional 
              de clínicas y centros médicos.
            </p>
          </div>
        </div>
      </section>

      {/* Focus Section */}
      <section className="py-16 md:py-24 bg-background" data-testid="focus-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-text-primary mb-4">
              Nuestro Enfoque
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {focusPoints.map((point, index) => (
              <Card 
                key={index} 
                className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`focus-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="icon-container mb-4">
                    <point.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
                    {point.title}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {point.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="process-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-text-primary mb-4">
              Cómo trabajamos
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {processSteps.map((step, index) => (
                <div 
                  key={index} 
                  className="relative"
                  data-testid={`process-step-${index}`}
                >
                  <Card className="bg-background border-0 h-full">
                    <CardContent className="p-6 text-center">
                      <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-4">
                        {index + 1}
                      </div>
                      <h3 className="font-heading font-semibold text-text-primary mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm text-text-secondary">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                  {index < processSteps.length - 1 && (
                    <ArrowRight className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-gray-300 h-6 w-6 z-10" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-primary/5" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-text-primary mb-8 text-center">
              Lo que nos diferencia
            </h2>
            <div className="space-y-4">
              {[
                "Comunicación clara y directa en cada etapa",
                "Documentación completa de cada intervención",
                "Enfoque en prevención para reducir emergencias",
                "App gratuita para gestión y seguimiento",
                "Atención en Santiago y regiones"
              ].map((value, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm"
                  data-testid={`value-item-${index}`}
                >
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0" />
                  <span className="text-text-primary">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-[#384091]" data-testid="nosotros-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Cotiza una evaluación
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Escríbenos y conversemos sobre tus equipos clínicos
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="bg-whatsapp hover:bg-whatsapp/90 text-white h-14 px-8 text-lg"
                data-testid="nosotros-cta-whatsapp"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                WhatsApp
              </Button>
            </a>
            <a href={`mailto:${EMAIL}`}>
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-white/10 border-white text-white hover:bg-white hover:text-[#384091] h-14 px-8 text-lg"
                data-testid="nosotros-cta-email"
              >
                Enviar correo
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
